import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";
import { ExhibitionGallery } from "@/components/ExhibitionGallery";
import { getArtworks, getExhibitions } from "@/lib/api";

const IMPACT = [
  "Six curated exhibitions and productions across Dallas–Fort Worth since 2023",
  "Fundraising partner to Quest for Brilliance, supporting young artists nationally and internationally",
  "HerArtistry 2024 raised funds for the Celebrating Life Foundation to feed families in need during the holidays",
  "Partnerships with Metrocare Dallas (Suicide Prevention Month), FGiii Fine Art Productions, Creative Visions, and Conscious Bodies",
  "Body-painting artist for DIFFA Dallas in 2023 and 2025, supporting HIV/AIDS awareness and research",
];

const SERVICES = [
  {
    n: "01",
    title: "Artist Development",
    body: "Creative identity, portfolio direction, artist statements, positioning, and strengthening how an artist communicates their work.",
  },
  {
    n: "02",
    title: "Exhibition Strategy",
    body: "Concept development, exhibition planning, presentation, audience experience, and turning creative ideas into cohesive projects.",
  },
  {
    n: "03",
    title: "Curated Event Assistance",
    body: "Taking your event concept, even in rough, early form, and shaping it into a fully realized flagship experience, from logistics to creative direction.",
  },
];

const VOICES = [
  {
    quote:
      "Seyoun did an amazing job with the event planning portion of the exhibit. I would love to see her demonstrate her skills as an emerging curator during the next art venture.",
    name: "Dr. Giraud Polite",
    role: "Artist",
  },
  {
    quote:
      "I'm so grateful to be part of this experience. I feel so motivated to continue with my work. Great things are happening, and I am so happy to be a part of it, especially for such a great cause as breast cancer.",
    name: "Shaleia Thompson",
    role: "Artist",
  },
];

export default function CuratorialPage() {
  useSEO(
    "Curatorial Practice & Portfolio · The House of Ego · Seyoun Taylor",
    "Exhibitions, curatorial philosophy, and professional accomplishments of curator Seyoun Taylor: The House of Ego, HerArtistry, Express Yourself, It Takes a Village, and more."
  );
  const [exhibitions, setExhibitions] = useState([]);
  const [installations, setInstallations] = useState([]);

  useEffect(() => {
    getExhibitions().then(setExhibitions).catch(() => {});
    getArtworks("Installations").then(setInstallations).catch(() => {});
  }, []);

  return (
    <main id="main-content" data-testid="curatorial-page">
      <section className="mx-auto max-w-7xl px-6 md:px-10 pt-40 md:pt-52 pb-16">
        <Reveal>
          <p className="eyebrow">Curatorial Practice &amp; Portfolio</p>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight text-ink max-w-4xl leading-[1.05]">
            Curating is an act of care.
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-neutral-600">
            Exhibition-making as world-building: sequencing works, space, and audience experience so a
            show unfolds like a story rather than a survey. Always in service of the artists and the
            communities the work speaks to.
          </p>
        </Reveal>
      </section>

      {/* Grant-panel-ready impact */}
      <section className="rule" data-testid="impact-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 md:py-24 grid gap-12 lg:grid-cols-[280px_1fr]">
          <Reveal>
            <p className="eyebrow">Professional Record</p>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight text-ink">
              Accomplishments &amp; impact
            </h2>
          </Reveal>
          <div>
            {IMPACT.map((line, i) => (
              <Reveal key={line} delay={i * 0.06}>
                <p className="py-5 border-t border-neutral-200 last:border-b text-neutral-700 leading-relaxed flex gap-4">
                  <span className="text-flame text-sm mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {line}
                </p>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <p className="mt-8 text-sm text-neutral-500">
                Grant panels and arts organizations may request a full CV, press kit, and exhibition
                documentation via the{" "}
                <Link to="/contact" data-testid="impact-contact-link" className="link-underline text-royal">
                  contact page
                </Link>
                .
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Exhibitions */}
      <section className="rule" data-testid="exhibitions-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <p className="eyebrow">Selected Exhibitions &amp; Projects</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink">
              Upcoming &amp; past events
            </h2>
          </Reveal>
          <div className="mt-16 space-y-20">
            {exhibitions.map((ex, i) => (
              <Reveal key={ex.slug}>
                <article
                  data-testid={`exhibition-${ex.slug}`}
                  className="grid gap-8 lg:grid-cols-2 items-start"
                >
                  <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                    <ExhibitionGallery title={ex.title} images={ex.images} fallback={ex.image_url} />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.2em] text-neutral-500">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays size={13} className="text-flame" /> {ex.date}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} className="text-flame" /> {ex.location}
                      </span>
                      {ex.status === "upcoming" ? (
                        <span className="bg-flame-soft text-flame px-2.5 py-1 tracking-[0.15em]">
                          Upcoming
                        </span>
                      ) : (
                        <span className="bg-neutral-100 text-neutral-500 px-2.5 py-1 tracking-[0.15em]">
                          Past
                        </span>
                      )}
                    </div>
                    <h3 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight text-ink">
                      {ex.title}
                    </h3>
                    <p className="mt-2 text-sm text-royal">{ex.role}</p>
                    <p className="mt-5 text-neutral-600 leading-relaxed">{ex.summary}</p>
                    {ex.philosophy && (
                      <p className="mt-4 text-sm leading-relaxed text-neutral-500 border-l-2 border-azure pl-4">
                        {ex.philosophy}
                      </p>
                    )}
                    {ex.credits.length > 0 && (
                      <ul className="mt-5 space-y-1.5 text-xs text-neutral-500">
                        {ex.credits.map((c) => (
                          <li key={c} className="flex gap-2">
                            <span className="text-flame">·</span>
                            {c}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Installations */}
      <section className="rule" data-testid="installations-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <p className="eyebrow">Installations &amp; Immersive Works</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl">
              Beyond the canvas
            </h2>
            <p className="mt-6 max-w-2xl text-neutral-600 leading-relaxed">
              Physical installations extend the practice into space: rooms and environments built to be
              walked through, felt, and remembered.
            </p>
          </Reveal>
          {installations.length === 0 ? (
            <Reveal delay={0.1}>
              <p
                className="mt-12 border border-dashed border-neutral-300 px-8 py-12 text-center text-sm text-neutral-400"
                data-testid="installations-placeholder"
              >
                Installation portfolio coming soon.
              </p>
            </Reveal>
          ) : (
            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="installations-grid">
              {installations.map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.08}>
                  <Link
                    to={`/gallery/${a.slug}`}
                    data-testid={`installation-${a.slug}`}
                    className="group block"
                  >
                    <div className="overflow-hidden bg-stone2 aspect-[4/3]">
                      <img
                        src={a.image_url}
                        alt={`${a.title}, installation by Seyoun Taylor`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="mt-4 font-serif text-2xl text-ink group-hover:text-royal transition-colors">
                      {a.title}
                    </h3>
                    {a.size && (
                      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-500">{a.size}</p>
                    )}
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Consulting services */}
      <section id="consulting" className="bg-royal-soft scroll-mt-24" data-testid="consulting-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <Reveal>
            <p className="eyebrow">1:1 Artist Consulting</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl">
              Personalized support for your creative practice
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed max-w-2xl">
              For emerging and established artists seeking clarity, strategy, stronger presentation,
              and intentional growth.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px bg-royal/10 border border-royal/10 md:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1} className="bg-white">
                <div className="p-8 md:p-10 h-full">
                  <p className="text-xs tracking-[0.25em] text-azure">{s.n}</p>
                  <h3 className="mt-5 font-serif text-2xl text-ink">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-neutral-600">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="consulting-inquire-button"
              className="mt-10 inline-flex items-center gap-2 bg-royal text-white px-8 py-4 text-sm tracking-wide hover:bg-royal-deep transition-colors"
            >
              Request consulting <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Voices */}
      <section className="rule" data-testid="curatorial-voices-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28 grid gap-12 md:grid-cols-2">
          {VOICES.map((v, i) => (
            <Reveal key={v.name} delay={i * 0.12}>
              <figure className="border-l-2 border-royal pl-6">
                <blockquote className="font-serif text-xl leading-relaxed text-ink">
                  “{v.quote}”
                </blockquote>
                <figcaption className="mt-6">
                  <p className="text-sm font-medium text-ink">{v.name}</p>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mt-1">{v.role}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
